import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import RenderElement from "../helpers/RenderElement";
import findElementByIdDraggable from "../helpers/findElementByIdDraggable";
import { DraggableComponentTypes } from "../types/draggable";
const EditorContext = createContext();
export const useEditor = () => useContext(EditorContext);
function EditorProvider({ children, page, initMode = "editor", backendApi = "", elements = [], }) {
    const [content, setContent] = useState(page?.data?.content ? JSON.parse(page?.data?.content.content) : []);
    const [mode, setMode] = useState(initMode);
    const [editorDevice, setEditorDevice] = useState("desktop");
    const [editorScale, setEditorScale] = useState(1);
    const [editorStylingDevice, setEditorStylingDevice] = useState("all");
    const [selectedElement, setSelectedElement] = useState(null);
    const [elementModalOpen, setElementModalOpen] = useState(false);
    const [elementAddDragActive, setElementAddDragActive] = useState(false);
    const [elementOverviewOpen, setElementOverviewOpen] = useState(false);
    const [elementType, setElementType] = useState(null);
    const [editableItem, setEditableItem] = useState(null);
    const [hasUpdate, setHasUpdate] = useState(false);
    const [iframeMount, setIframeMount] = useState(null);
    const [lastSelectedEditableTime, setLastSelectedEditableTime] = useState(0);
    const [dragActive, setDragActive] = useState(false);
    const dragOverComponent = useRef(null);
    const [dragContextMenu, setDragContextMenu] = useState(null);
    const [draggableHtmlEditor, setDraggableHtmlEditor] = useState(null);
    const [dragElementMenu, setDragElementMenu] = useState(null);
    const addElement = (element, index) => {
        if (index >= -1) {
            const contentArray = [...content];
            contentArray.splice(index === -1 ? 0 : index, 0, element);
            setContent(contentArray);
        }
        else {
            setContent([...content, element]);
        }
        setElementAddDragActive(false);
        setHasUpdate(true);
        setElementModalOpen(false);
    };
    const changeElementPostion = (elementId, action) => {
        const contentArray = [...content];
        const element = content.find((c) => c.id === elementId);
        const elementIndex = content.findIndex((c) => c.id === elementId);
        contentArray.splice(elementIndex, 1);
        if (action === "up")
            contentArray.splice(elementIndex - 1, 0, element);
        if (action === "down")
            contentArray.splice(elementIndex + 1, 0, element);
        setContent(contentArray);
        setHasUpdate(true);
    };
    const duplicateElement = (elementId) => {
        const contentArray = [...content];
        const element = content.find((c) => c.id === elementId);
        const elementIndex = content.findIndex((c) => c.id === elementId);
        const duplatedElement = {
            ...element,
            id: uuidv4(),
        };
        contentArray.splice(elementIndex + 1, 0, duplatedElement);
        setContent(contentArray);
        setHasUpdate(true);
    };
    const removeElement = (elementId) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === elementId);
        contentArray.splice(elementIndex, 1);
        setContent(contentArray);
        setHasUpdate(true);
    };
    const updateElementProp = (value, key = null, elementId = null) => {
        const contentArray = [...content];
        const selectedElementId = elementId ? elementId : selectedElement;
        const elementIndex = content.findIndex((c) => c.id === selectedElementId);
        if (elementIndex === -1)
            return;
        const editableItemKey = key || editableItem.getAttribute("editor-key");
        if (!contentArray[elementIndex].props) {
            contentArray[elementIndex] = {
                ...contentArray[elementIndex],
                props: {},
            };
        }
        if (!contentArray[elementIndex].props?.[editableItemKey]) {
            contentArray[elementIndex].props = {
                ...contentArray[elementIndex].props,
                [editableItemKey]: {},
            };
        }
        contentArray[elementIndex].props = {
            ...contentArray[elementIndex].props,
            [editableItemKey]: {
                ...contentArray[elementIndex].props?.[editableItemKey],
                ...value,
            },
        };
        setContent(contentArray);
        setHasUpdate(true);
    };
    const deleteRepeatElementProp = (elementId, groupInput, indexInput) => {
        const itemData = groupInput && indexInput
            ? []
            : editableItem?.getAttribute("editor-deletekey").split("|");
        const group = groupInput || itemData[1];
        const index = indexInput || itemData[2];
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === elementId || itemData[0]);
        if (elementIndex === -1)
            return;
        setEditableItem(null);
        Object.keys(contentArray[elementIndex].props).forEach((itemKey) => {
            if (itemKey.startsWith(`${group}-${index}`)) {
                delete contentArray[elementIndex].props[itemKey];
            }
        });
        const repeatItemIndex = contentArray[elementIndex].props[group]?.items?.indexOf(index);
        if (repeatItemIndex > -1) {
            contentArray[elementIndex].props[group]?.items.splice(repeatItemIndex, 1);
        }
        setContent(contentArray);
        setHasUpdate(true);
    };
    useEffect(() => {
        if (mode === "editor") {
            RenderElement.setEditor(true);
        }
        else {
            RenderElement.setEditor(false);
        }
    }, [mode]);
    const draggableAddComponent = (draggableBoxId, componentType, elementId, data = null) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === elementId);
        if (elementIndex === -1)
            return;
        const currentParentElement = findElementByIdDraggable(data?.id.split("/").slice(0, -1).join("/"), contentArray[elementIndex]?.props);
        const indexOfCurrentElement = currentParentElement?.children?.findIndex((c) => c.id === data?.id);
        if (indexOfCurrentElement &&
            dragOverComponent.current &&
            data.id.split("/").slice(0, -1).join("/") ===
                dragOverComponent.current.split("/").slice(0, -1).join("/")) {
            const currentChilds = [...currentParentElement.children];
            const indexOfDragOverElement = currentParentElement.children.findIndex((c) => c.id === dragOverComponent.current);
            let elementToMove = currentChilds.splice(indexOfCurrentElement, 1)[0];
            currentChilds.splice(indexOfDragOverElement, 0, elementToMove);
            currentParentElement.children = currentChilds;
            dragOverComponent.current = null;
        }
        else {
            if (!contentArray[elementIndex].props) {
                contentArray[elementIndex] = {
                    ...contentArray[elementIndex],
                    props: {},
                };
            }
            if (!contentArray[elementIndex].props?.children) {
                contentArray[elementIndex].props = {
                    ...contentArray[elementIndex].props,
                    id: 1,
                    children: [],
                };
            }
            if (draggableBoxId.includes("/") &&
                draggableBoxId.split("/").length > 1) {
                const parentElement = findElementByIdDraggable(draggableBoxId, contentArray[elementIndex]?.props);
                const newDraggableBoxId = `${draggableBoxId}/${uuidv4()}`;
                parentElement.children = [
                    ...(parentElement.children || []),
                    {
                        id: newDraggableBoxId,
                        type: componentType,
                        ...(data && data.props && { props: data.props }),
                        ...(componentType === "grid" && {
                            props: {
                                gridMobile: "grid-cols-2",
                                gridTablet: "md:grid-cols-2",
                                gridDesktop: "lg:grid-cols-2",
                            },
                            children: [
                                {
                                    id: `${newDraggableBoxId}/${uuidv4()}`,
                                    type: DraggableComponentTypes.GridItem,
                                },
                                {
                                    id: `${newDraggableBoxId}/${uuidv4()}`,
                                    type: DraggableComponentTypes.GridItem,
                                },
                            ],
                        }),
                    },
                ];
            }
            else {
                const newDraggableBoxId = `${draggableBoxId}/${uuidv4()}`;
                contentArray[elementIndex].props = {
                    ...contentArray[elementIndex].props,
                    children: [
                        ...contentArray[elementIndex].props?.children,
                        {
                            id: newDraggableBoxId,
                            type: componentType,
                            ...(data && data.props && { props: data.props }),
                            ...(componentType === "grid" && {
                                props: {
                                    gridMobile: "grid-cols-2",
                                    gridTablet: "md:grid-cols-2",
                                    gridDesktop: "lg:grid-cols-2",
                                },
                                children: [
                                    {
                                        id: `${newDraggableBoxId}/${uuidv4()}`,
                                        type: DraggableComponentTypes.GridItem,
                                    },
                                    {
                                        id: `${newDraggableBoxId}/${uuidv4()}`,
                                        type: DraggableComponentTypes.GridItem,
                                    },
                                ],
                            }),
                        },
                    ],
                };
            }
            if (data) {
                const elementParentId = data.id.split("/").slice(0, -1).join("/");
                let currentParentElement = elementParentId === "1"
                    ? contentArray[elementIndex]?.props
                    : findElementByIdDraggable(elementParentId, contentArray[elementIndex]?.props);
                const indexOfCurrentElement = currentParentElement?.children.findIndex((c) => c.id === data.id);
                currentParentElement?.children?.splice(indexOfCurrentElement, 1);
            }
        }
        setContent(contentArray);
        setHasUpdate(true);
        setDragActive(false);
    };
    const draggableUpdateElementProp = (value, id = null) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === selectedElement);
        if (elementIndex === -1)
            return;
        const editableItemKey = id || editableItem?.getAttribute("editor-key");
        const editableElement = findElementByIdDraggable(editableItemKey, contentArray[elementIndex]?.props);
        if (!editableElement?.props) {
            editableElement.props = {};
        }
        editableElement.props = {
            ...editableElement?.props,
            ...value,
        };
        setContent(contentArray);
        setHasUpdate(true);
    };
    const draggableFindElement = (editableItemKey, data) => {
        const contentObj = data || content;
        const elementIndex = contentObj.findIndex((c) => c.id === selectedElement);
        if (elementIndex === -1)
            return;
        return findElementByIdDraggable(editableItemKey, contentObj[elementIndex]?.props);
    };
    const draggableItemUpdate = (editableItemKey, action, elementId) => {
        const contentArray = [...content];
        const elementIndex = content.findIndex((c) => c.id === elementId);
        if (elementIndex === -1)
            return;
        const currentParentElement = findElementByIdDraggable(editableItemKey.split("/").slice(0, -1).join("/"), contentArray[elementIndex]?.props);
        if (!currentParentElement)
            return;
        const indexOfCurrentElement = currentParentElement?.children.findIndex((c) => c.id === editableItemKey);
        const currentChilds = currentParentElement?.children
            ? [...currentParentElement?.children]
            : [];
        if (action === "remove") {
            if (editableItem?.getAttribute("editor-key") === editableItemKey) {
                setEditableItem(null);
            }
            currentChilds.splice(indexOfCurrentElement, 1);
        }
        if (action === "duplicate") {
            const duplicatedElement = {
                ...currentParentElement?.children[indexOfCurrentElement],
                id: `${currentParentElement.id}/${uuidv4()}`,
            };
            currentChilds.push(JSON.parse(JSON.stringify(duplicatedElement).replaceAll(currentParentElement?.children[indexOfCurrentElement].id, duplicatedElement.id)));
        }
        if (["up", "down"].includes(action)) {
            let elementToMove = currentChilds.splice(indexOfCurrentElement, 1)[0];
            currentChilds.splice(action === "up" ? indexOfCurrentElement - 1 : indexOfCurrentElement + 1, 0, elementToMove);
        }
        if (action === "changeToIcon") {
            const updatedElement = {
                ...currentParentElement?.children[indexOfCurrentElement],
                type: DraggableComponentTypes.Icon,
            };
            currentChilds[indexOfCurrentElement] = updatedElement;
        }
        currentParentElement.children = currentChilds;
        setContent(contentArray);
        setHasUpdate(true);
    };
    const draggableGridModify = (editableItemKey, action, actionValue = {}) => {
        const contentArray = [...content];
        const gridItem = draggableFindElement(editableItemKey, contentArray);
        if (action === "add") {
            gridItem.children = [
                ...gridItem.children,
                {
                    id: `${editableItemKey}/${uuidv4()}`,
                    type: DraggableComponentTypes.GridItem,
                },
            ];
        }
        if (action === "remove") {
            gridItem.children = gridItem.children.filter((c) => c.id !== actionValue.id);
        }
        if (action === "update") {
            gridItem.props = {
                ...gridItem.props,
                ...actionValue,
            };
        }
        if (action === "copy") {
            const element = gridItem.children.find((c) => c.id === actionValue.id);
            let newElement = {
                ...element,
                id: `${editableItemKey}/${uuidv4()}`,
                type: DraggableComponentTypes.GridItem,
            };
            newElement = JSON.parse(JSON.stringify(element).replaceAll(element.id, `${editableItemKey}/${uuidv4()}`));
            gridItem.children = [...gridItem.children, newElement];
        }
        setContent(contentArray);
        setHasUpdate(true);
    };
    return (_jsx(EditorContext.Provider, { value: {
            elements,
            content,
            addElement,
            selectedElement,
            setSelectedElement,
            changeElementPostion,
            duplicateElement,
            removeElement,
            elementModalOpen,
            setElementModalOpen,
            elementAddDragActive,
            setElementAddDragActive,
            updateElementProp,
            deleteRepeatElementProp,
            mode,
            setMode,
            editorDevice,
            setEditorDevice,
            editorScale,
            setEditorScale,
            editorStylingDevice,
            setEditorStylingDevice,
            editableItem,
            setEditableItem,
            backendApi,
            hasUpdate,
            page,
            setHasUpdate,
            elementType,
            setElementType,
            iframeMount,
            setIframeMount,
            lastSelectedEditableTime,
            setLastSelectedEditableTime,
            draggableAddComponent,
            dragActive,
            setDragActive,
            draggableUpdateElementProp,
            draggableFindElement,
            draggableItemUpdate,
            draggableGridModify,
            dragOverComponent,
            dragContextMenu,
            setDragContextMenu,
            dragElementMenu,
            setDragElementMenu,
            elementOverviewOpen,
            setElementOverviewOpen,
            draggableHtmlEditor,
            setDraggableHtmlEditor,
            setContent,
        }, children: children }));
}
export default EditorProvider;
//# sourceMappingURL=EditorProvider.js.map